import { useState } from "react";
import { Layout } from "../components/Layout";
import { API, apiClient } from "../lib/api";
import { toast } from "sonner";
import { Newspaper, ArrowUpRight, ArrowRight, Check, Play } from "lucide-react";

/**
 * Featured press coverage. Article photos come from each story's Open Graph
 * image, resolved once by the API and cached. If an outlet blocks the lookup,
 * the card falls back to an on-brand styled thumbnail.
 */
const NEWS = [
  {
    outlet: "Salt Lake Tribune",
    tone: "from-[#0b3a4f] to-[#04212f]",
    title:
      "A Utah city's youngest council member gets a win in his campaign against Flock",
    excerpt:
      "The Vineyard City Council votes 4-0 to remove Flock license plate cameras from city property after a push led by Councilmember Jacob Wood.",
    url: "https://www.sltrib.com/news/2026/10/29/vineyard-small-city-utah-county/",
    image:
      "https://www.sltrib.com/resizer/v2/L2QAFB2665ACNKFEDIBNVNAGK4.jpg?auth=18701fa62a2587811678a6a4d55e1a426404cab74978e479e634ab54bdaa26e5&width=1200&height=630&quality=88&smart=true",
  },
  {
    outlet: "KSL",
    tone: "from-[#0a3a52] to-[#052230]",
    title: "Vineyard council overhauls the city's punitive parking rules",
    excerpt:
      "Council members vote to replace punitive parking penalties with resident-friendly policies across Vineyard.",
    url: "https://www.ksl.com/article/51448763/vineyard-council-overhauls-citys-punitive-parking",
  },
  {
    outlet: "KSL",
    tone: "from-[#123a4d] to-[#061a26]",
    title: "Vineyard Councilmember Jacob Wood fulfills campaign promise",
    excerpt:
      "Councilmember Jacob Wood makes good on a key campaign promise, leading an overhaul of how Vineyard approaches parking for residents and families.",
    url: "https://www.ksl.com/article/news/utah/vineyard-parking-overhaul-city-council/51590188",
  },
  {
    outlet: "Herald Extra",
    tone: "from-[#124454] to-[#061a26]",
    title:
      "Vineyard City Council votes to remove parking restrictions on 300 West frontage road",
    excerpt:
      "Parking restrictions along the 300 West Vineyard frontage road are lifted following a city council vote.",
    url: "https://www.heraldextra.com/news/2026/jan/21/vineyard-city-council-votes-to-remove-parking-restrictions-on-300-west-vineyard-frontage-road/",
  },
  {
    outlet: "KSL",
    tone: "from-[#0a3a52] to-[#04212f]",
    title:
      "24-year-old makes history as Vineyard's youngest and first Black city council member",
    excerpt:
      "Jacob Wood is sworn in as the youngest person — and the first Black council member — ever elected to serve Vineyard City.",
    url: "https://www.ksl.com/article/news/utah/politics/24-year-old-makes-history-as-vineyards-youngest-and-first-black-city-council-member/51588375",
  },
  {
    outlet: "ABC4",
    tone: "from-[#0e3346] to-[#04212f]",
    title: "Vineyard, Utah city hall referendum",
    excerpt:
      "A citizen referendum over Vineyard's proposed new city hall draws attention across the Wasatch Front.",
    url: "https://www.abc4.com/news/wasatch-front/vineyard-utah-city-hall-referendum/",
  },
];

/**
 * Video coverage, newest first. Thumbnails come straight from YouTube; the
 * player only loads once a visitor presses play.
 */
const VIDEOS = [
  {
    id: "IFtR_lN2Q5I",
    outlet: "FOX 13",
    date: "Oct. 28, 2026",
    title: "Vineyard council unanimously resolve to end relationship with Flock cameras",
  },
  {
    id: "iipgOrmGcY0",
    outlet: "KUTV 2",
    date: "Oct. 27, 2026",
    title: "Vineyard council considers resolution calling for removal of Flock cameras",
  },
  {
    id: "kNDO-TtOamY",
    outlet: "KSL",
    date: "Feb. 14, 2026",
    title:
      "Vineyard Councilmember Jacob Wood fulfills campaign promise, overhauls city's punitive parking",
  },
  {
    id: "M1sDWhLHB78",
    outlet: "ABC4",
    date: "Apr. 10, 2025",
    title: "Vineyard residents pushing back against $35 million city bond",
  },
];

// The API looks up each article's share photo once, caches it, and redirects.
const ogImageUrl = (articleUrl) =>
  `${API}/press/og-image?url=${encodeURIComponent(articleUrl)}`;

export default function PressPage() {
  return (
    <Layout>
      <section
        className="relative pt-24 md:pt-32 pb-20 px-5 md:px-8"
        data-testid="press-page"
      >
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12 md:mb-16 fade-up">
            <div className="seal mb-6">
              <Newspaper size={12} /> Press &amp; News
            </div>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-white tracking-tight mb-5"
              data-testid="press-heading"
            >
              In the News
            </h1>
            <p className="text-white/60 max-w-2xl mx-auto text-base md:text-lg leading-relaxed">
              Coverage of Councilmember Wood's work for Vineyard from across
              Utah news.
            </p>
          </div>

          <NewsGrid />
        <VideoGrid />
          <PressRequestForm />
        </div>
      </section>
    </Layout>
  );
}

/* ===================== NEWS GRID ===================== */
const NewsGrid = () => (
  <div
    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
    data-testid="news-grid"
  >
    {NEWS.map((n, i) => (
      <NewsCard key={n.url} news={n} index={i} />
    ))}
  </div>
);

const NewsCard = ({ news, index }) => {
  const [imgOk, setImgOk] = useState(true);

  return (
    <a
      href={news.url}
      target="_blank"
      rel="noopener noreferrer"
      className="group glass-strong rounded-2xl overflow-hidden hover-lift fade-up flex flex-col"
      style={{ animationDelay: `${Math.min(index, 8) * 70}ms` }}
      data-testid={`news-card-${index}`}
    >
      {/* Thumbnail — real article photo with on-brand fallback */}
      <div
        className={`relative aspect-[16/9] bg-gradient-to-br ${news.tone} flex items-center justify-center overflow-hidden`}
      >
        {imgOk ? (
          <img
            src={news.image || ogImageUrl(news.url)}
            alt={news.title}
            loading={index < 3 ? "eager" : "lazy"}
            decoding="async"
            referrerPolicy="no-referrer"
            onError={() => setImgOk(false)}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            data-testid={`news-image-${index}`}
          />
        ) : (
          <>
            <div className="absolute inset-0 opacity-30 bg-[radial-gradient(60%_60%_at_30%_20%,rgba(245,196,60,0.25),transparent_70%)]" />
            <span className="relative text-white/90 text-2xl md:text-3xl font-semibold tracking-tight drop-shadow-lg">
              {news.outlet}
            </span>
          </>
        )}

        {/* Outlet badge + open indicator (always visible, over photo or fallback) */}
        <span className="absolute top-3 left-3 z-10 inline-flex items-center gap-1.5 rounded-full bg-black/45 backdrop-blur px-2.5 py-1 text-[10px] tracking-[0.18em] uppercase text-white/90">
          {news.outlet}
        </span>
        <span className="absolute top-3 right-3 z-10 w-7 h-7 rounded-full bg-black/45 backdrop-blur flex items-center justify-center text-white/80 group-hover:text-yellow-400 transition-colors">
          <ArrowUpRight size={15} />
        </span>
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
      </div>

      {/* Body */}
      <div className="p-5 md:p-6 flex flex-col flex-1">
        <h3 className="text-white font-semibold leading-snug mb-3 group-hover:text-yellow-400 transition-colors">
          {news.title}
        </h3>
        <p className="text-white/60 text-sm leading-relaxed mb-5 flex-1">
          {news.excerpt}
        </p>
        <span className="inline-flex items-center gap-1.5 text-yellow-400/90 text-sm font-medium">
          Read More <ArrowRight size={14} />
        </span>
      </div>
    </a>
  );
};

/* ===================== VIDEO GRID ===================== */
const VideoGrid = () => (
  <div className="mt-16 md:mt-24" data-testid="video-section">
    <div className="text-center mb-10">
      <div className="seal mb-5">
        <Play size={12} /> On Video
      </div>
      <h2 className="text-3xl md:text-4xl font-semibold text-white tracking-tight mb-3">
        Video Coverage
      </h2>
      <p className="text-white/60 text-base max-w-xl mx-auto">
        TV news coverage of Councilmember Wood's work for Vineyard.
      </p>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-testid="video-grid">
      {VIDEOS.map((v, i) => (
        <VideoCard key={v.id} video={v} index={i} />
      ))}
    </div>
  </div>
);

const VideoCard = ({ video, index }) => {
  const [playing, setPlaying] = useState(false);

  return (
    <div
      className="group glass-strong rounded-2xl overflow-hidden fade-up flex flex-col"
      style={{ animationDelay: `${Math.min(index, 8) * 70}ms` }}
      data-testid={`video-card-${index}`}
    >
      <div className="relative aspect-video bg-black overflow-hidden">
        {playing ? (
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${video.id}?autoplay=1&rel=0`}
            title={video.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="absolute inset-0 w-full h-full"
          />
        ) : (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            className="absolute inset-0 w-full h-full"
            aria-label={`Play video: ${video.title}`}
            data-testid={`video-play-${index}`}
          >
            <img
              src={`https://i.ytimg.com/vi/${video.id}/hqdefault.jpg`}
              alt={video.title}
              loading="lazy"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent" />
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="w-16 h-16 rounded-full bg-black/55 backdrop-blur flex items-center justify-center text-white group-hover:text-yellow-400 group-hover:scale-110 transition-all">
                <Play size={28} className="ml-1" fill="currentColor" />
              </span>
            </span>
            <span className="absolute top-3 left-3 z-10 inline-flex items-center rounded-full bg-black/45 backdrop-blur px-2.5 py-1 text-[10px] tracking-[0.18em] uppercase text-white/90">
              {video.outlet}
            </span>
          </button>
        )}
      </div>
      <div className="p-5 md:p-6 flex flex-col flex-1">
        <span className="text-white/45 text-xs uppercase tracking-[0.18em] mb-2">
          {video.date}
        </span>
        <h3 className="text-white font-semibold leading-snug mb-4 flex-1">
          {video.title}
        </h3>
        <a
          href={`https://www.youtube.com/watch?v=${video.id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-yellow-400/90 text-sm font-medium hover:text-yellow-400"
        >
          Watch on YouTube <ArrowUpRight size={14} />
        </a>
      </div>
    </div>
  );
};

/* ===================== PRESS / MEDIA REQUEST FORM ===================== */
const REQUEST_TYPES = [
  "Interview request",
  "Statement or quote",
  "Comment on a story",
  "Background / information",
  "Other",
];

function PressRequestForm() {
  const empty = {
    name: "",
    organization: "",
    email: "",
    phone: "",
    request_type: REQUEST_TYPES[0],
    message: "",
  };
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const mailtoFallback = () => {
    const subject = encodeURIComponent(
      `Press Request (${form.request_type}) — ${form.organization || form.name}`
    );
    const body = encodeURIComponent(
      `Name: ${form.name}\nOrganization: ${form.organization}\nEmail: ${form.email}\nPhone: ${form.phone || "—"}\nRequest type: ${form.request_type}\n\n${form.message}`
    );
    window.location.href = `mailto:jacobw@vineyardutah.gov?subject=${subject}&body=${body}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.organization || !form.email || !form.message) {
      toast.error("Please fill out the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await apiClient.post("/press", form);
      setSent(true);
      setForm(empty);
    } catch (err) {
      // Backend unreachable — fall back to the visitor's email client.
      toast.error("Couldn't reach the server — opening your email app instead.");
      mailtoFallback();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-16 md:mt-24 max-w-3xl mx-auto" data-testid="press-form-wrap">
      <div className="text-center mb-8">
        <div className="seal mb-5">
          <Newspaper size={12} /> Media &amp; Press
        </div>
        <h2 className="text-3xl md:text-4xl font-semibold text-white tracking-tight mb-3">
          Make a Press Request
        </h2>
        <p className="text-white/60 text-base max-w-xl mx-auto">
          Members of the media — request an interview, a statement, a comment,
          or background on Councilmember Wood's work. Tell us what you need and
          we'll follow up.
        </p>
      </div>

      {sent ? (
        <div
          className="glass-strong rounded-2xl p-10 text-center"
          data-testid="press-form-success"
        >
          <div className="w-14 h-14 rounded-full bg-yellow-400/15 border border-yellow-400/40 text-yellow-400 flex items-center justify-center mx-auto mb-5">
            <Check size={26} />
          </div>
          <h3 className="text-white text-xl font-medium mb-2">Request received.</h3>
          <p className="text-white/60 text-sm mb-6">
            Thank you for reaching out. We'll respond to your request as soon as
            possible.
          </p>
          <button
            onClick={() => setSent(false)}
            className="btn-ghost rounded-full px-6 py-2.5 text-sm tracking-wider"
            data-testid="press-form-reset"
          >
            Submit another
          </button>
        </div>
      ) : (
        <form
          onSubmit={submit}
          className="glass-strong rounded-2xl p-6 md:p-9 space-y-5"
          data-testid="press-form"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Name *">
              <input
                type="text"
                value={form.name}
                onChange={set("name")}
                className="press-input"
                data-testid="press-name"
                required
              />
            </Field>
            <Field label="Organization / Outlet *">
              <input
                type="text"
                value={form.organization}
                onChange={set("organization")}
                className="press-input"
                data-testid="press-org"
                required
              />
            </Field>
            <Field label="Email *">
              <input
                type="email"
                value={form.email}
                onChange={set("email")}
                className="press-input"
                data-testid="press-email"
                required
              />
            </Field>
            <Field label="Phone (optional)">
              <input
                type="tel"
                value={form.phone}
                onChange={set("phone")}
                className="press-input"
                data-testid="press-phone"
              />
            </Field>
          </div>
          <Field label="What are you requesting? *">
            <select
              value={form.request_type}
              onChange={set("request_type")}
              className="press-input press-select"
              data-testid="press-request-type"
            >
              {REQUEST_TYPES.map((t) => (
                <option key={t} value={t} className="bg-[#04212f]">
                  {t}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Details of your request *">
            <textarea
              rows={6}
              value={form.message}
              onChange={set("message")}
              placeholder="What are you working on, your deadline, and anything specific you're asking for."
              className="press-input resize-none"
              data-testid="press-message"
              required
            />
          </Field>
          <button
            type="submit"
            disabled={submitting}
            className="w-full flex items-center justify-center gap-2 btn-accent px-7 py-4 text-[15px] disabled:opacity-60"
            data-testid="press-submit"
          >
            <span>{submitting ? "Sending…" : "Submit Request"}</span>
            <ArrowRight size={18} />
          </button>
        </form>
      )}
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-[0.18em] text-white/50 mb-2 block">
      {label}
    </span>
    {children}
  </label>
);
